import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  message,
  Avatar,
  Select,
  Popconfirm,
  Modal,
} from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, EditOutlined } from '@ant-design/icons';
import { getApiResponseErrorMessage, getAxiosErrorMessage } from '../utils/error';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import UserService from '../services/user';
import RoleService from '../services/role';
import { optionService } from '../services/option';
import useDocumentTitle from '../hooks/useDocumentTitle';
import CUSTOMER_CATEGORY_OPTIONS from '../config/customer-categories';
import WeChatLogin from '../components/WeChatLogin';
import wechatService from '../services/wechat';
import authService from '../services/auth';
import MediaSelector from '../components/MediaLibrary/MediaSelector';
import mediaService from '../services/media';

const { Title, Text } = Typography;
const { Option } = Select;

interface UserProfileFormData {
  username: string;
  email?: string;
  phone: string;
  client_type?: string;
  customer_categories?: string[];
  contact_name?: string;
  channel?: string;
  source?: string;
  group_number?: string;
  notification_emails?: string;
  parent_id?: number | null;
  current_password?: string;
  new_password?: string;
  confirm_password?: string;
  avatar?: string;
}

const UserProfile: React.FC = () => {
  useDocumentTitle('个人设置 - 用户');
  const { state, updateUserInfo } = useAuth();
  const { user } = state;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [clientTypeOptions, setClientTypeOptions] = useState<string[]>([]);
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [targetUser, setTargetUser] = useState<any>(null); // 要编辑的目标用户
  const [roles, setRoles] = useState<any[]>([]); // 所有可用角色
  const [resetPasswordForm] = Form.useForm(); // 密码重置表单
  const [rolesLoading, setRolesLoading] = useState(false);
  const [parentCandidates, setParentCandidates] = useState<any[]>([]);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [salesCandidates, setSalesCandidates] = useState<any[]>([]);
  const [wechatBindVisible, setWechatBindVisible] = useState(false);
  const [wechatMpBindVisible, setWechatMpBindVisible] = useState(false);
  const [unbindLoading, setUnbindLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(undefined);
  const [avatarMediaIds, setAvatarMediaIds] = useState<number[]>([]);
  const [avatarModalVisible, setAvatarModalVisible] = useState(false);

  // 获取URL参数中的用户ID，如果存在则表示编辑其他用户
  const editingUserId = searchParams.get('userId');
  const isEditingOtherUser = editingUserId && editingUserId !== user?.id?.toString();
  // 判断是否客户角色（用于显示客户相关字段）
  const watchedRoles = Form.useWatch('roles', form) as any;
  const rolesArray = React.useMemo(() => (Array.isArray(watchedRoles) ? watchedRoles : (watchedRoles ? [watchedRoles] : [])), [watchedRoles]);
  const isCustomerRole = React.useMemo(() => {
    const customerAliases = ['customer', 'client', 'peers'];
    // 在编辑他人且表单已选择新角色时，优先根据表单选择判断
    if (rolesArray && rolesArray.length > 0 && isEditingOtherUser) {
      const names = rolesArray.map((r: any) => String(r));
      return names.some((n: string) => customerAliases.includes(n));
    }
    // 否则根据当前用户/目标用户的角色判断
    const rolesArr: any[] = (isEditingOtherUser ? (targetUser?.roles || []) : (user?.roles || [])) as any[];
    const names = (rolesArr || []).map((r: any) => r?.name).filter(Boolean);
    return names.some((n: string) => customerAliases.includes(n));
  }, [rolesArray, isEditingOtherUser, targetUser, user]);

  // 仅 client 角色需要“通知邮箱”字段
  const showNotificationEmails = React.useMemo(() => {
    let names: string[] = [];
    if (isEditingOtherUser && rolesArray && rolesArray.length > 0) {
      names = rolesArray.map((r: any) => String(r));
    } else {
      const rolesArr: any[] = (isEditingOtherUser ? (targetUser?.roles || []) : (user?.roles || [])) as any[];
      names = (rolesArr || []).map((r: any) => r?.name).filter(Boolean) as string[];
    }
    return names.includes('client');
  }, [isEditingOtherUser, rolesArray, targetUser, user]);

  // 非 client/viewer 才能设置上级
  const showParentSetting = React.useMemo(() => {
    // 优先使用表单中选中的角色（编辑他人时），实现动态联动
    let names: string[] = [];
    if (isEditingOtherUser && rolesArray && rolesArray.length > 0) {
      names = rolesArray.map((r: any) => String(r));
    } else {
      const rolesArr: any[] = (isEditingOtherUser ? (targetUser?.roles || []) : (user?.roles || [])) as any[];
      names = (rolesArr || []).map((r: any) => r?.name).filter(Boolean) as string[];
    }

    // sales_manager 与 coordinator_manager 不需要设置上级
    if (names.includes('sales_manager') || names.includes('coordinator_manager')) return false;

    // 以下角色不展示上级：client/viewer/admin/peers
    if (names.includes('client') || names.includes('viewer') || names.includes('admin') || names.includes('peers')) return false;

    // 其余（含 sales、coordinator）展示上级选择
    return true;
  }, [isEditingOtherUser, rolesArray, targetUser, user]);

  // 依角色过滤可选上级：
  // - sales 的上级从 sales_manager 选择
  // - coordinator 的上级从 coordinator_manager 选择
  const selectedRoleNames = React.useMemo(
    () => (Array.isArray(rolesArray) ? rolesArray.map((r: any) => String(r)) : []),
    [rolesArray]
  );
  const filteredParentCandidates = React.useMemo(() => {
    const names = isEditingOtherUser && selectedRoleNames.length > 0
      ? selectedRoleNames
      : ((isEditingOtherUser ? (targetUser?.roles || []) : (user?.roles || [])) as any[])
        .map((r: any) => r?.name)
        .filter(Boolean);
    if (names.includes('sales')) {
      return parentCandidates.filter(
        (u: any) => Array.isArray(u.roles) && u.roles.some((r: any) => r?.name === 'sales_manager')
      );
    }
    if (names.includes('coordinator')) {
      return parentCandidates.filter(
        (u: any) => Array.isArray(u.roles) && u.roles.some((r: any) => r?.name === 'coordinator_manager')
      );
    }
    return parentCandidates;
  }, [isEditingOtherUser, selectedRoleNames, targetUser, user, parentCandidates]);

  useEffect(() => {
    // 主动获取完整的用户数据（包含 meta 信息）
    const loadUserData = async () => {
      const targetUserId = isEditingOtherUser ? editingUserId : user?.id;

      if (targetUserId) {
        try {
          let userData;
          if (isEditingOtherUser) {
            // 编辑其他用户：通过用户ID获取用户信息
            const response = await UserService.getUser(parseInt(editingUserId!));
            if (response.success && response.data) {
              userData = response.data;
              setTargetUser(userData);
              console.log('编辑其他用户 - 获取到的用户数据:', userData);
              console.log('编辑其他用户 - metas数据:', (userData as any).metas);
            } else {
              message.error('无法获取用户信息');
              return;
            }
          } else {
            // 编辑自己：获取当前用户完整信息
            userData = await UserService.getCurrentUser();
            console.log('编辑自己 - 获取到的用户数据:', userData);
          }

          // 类型断言，因为后端返回的数据包含 metas 关系
          const userWithMetas = userData as any;

          // 提取表单数据
          const formData = {
            username: userWithMetas.username,
            email: userWithMetas.email || '',
            phone: userWithMetas.phone || '',
            client_type: userWithMetas.client_type || '',
            contact_name: userWithMetas.contact_name || '',
            source: userWithMetas.source || '',
            group_number: userWithMetas.group_number || '',
            avatar: (userWithMetas as any).avatar || (userWithMetas as any).wechat_avatar || '',
            customer_categories: (() => {
              const raw = (userWithMetas as any).customer_categories;
              if (!raw) return [] as string[];
              try {
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return String(raw).split(',').map((s: string) => s.trim()).filter(Boolean);
              }
            })(),
            notification_emails: (userWithMetas as any).notification_emails || '',
          };

          console.log('提取的表单数据:', formData);

          // 设置表单值，包含 meta 数据 + 上级用户
          form.setFieldsValue({
            ...formData,
            parent_id: (userWithMetas as any).parent_id || undefined,
          });
          setAvatarPreview(formData.avatar || undefined);
          console.log('表单已设置值');
        } catch (error) {
          console.error('获取用户数据失败:', error);
          // 降级到使用 AuthContext 的数据（仅在编辑自己时）
          if (!isEditingOtherUser && user) {
            form.setFieldsValue({
              username: user.username || '',
              email: user.email || '',
              phone: user.phone || '',
              client_type: user.client_type || '',
              contact_name: user.contact_name || '',
              source: user.source || '',
              group_number: (user as any).group_number || '',
              notification_emails: (user as any).notification_emails || '',
              avatar: (user as any).avatar || (user as any).wechat_avatar || '',
              customer_categories: Array.isArray((user as any).customer_categories) ? (user as any).customer_categories : [],
            });
            setAvatarPreview((user as any).avatar || (user as any).wechat_avatar || undefined);
          }
        }
      }
    };

    loadUserData();
  }, [user, form, editingUserId, isEditingOtherUser]);

  // 加载上级候选用户
  useEffect(() => {
    const loadCandidates = async () => {
      try {
        const res = await UserService.getUsers();
        if (res.success && Array.isArray(res.data)) {
          setParentCandidates(res.data);
        }
      } catch { }
    };
    loadCandidates();
  }, []);

  // 加载数据设置选项
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [clientTypes, sources] = await Promise.all([
          optionService.get('customer_types', []),
          optionService.get('customer_sources', []),
        ]);

        setClientTypeOptions(Array.isArray(clientTypes) ? clientTypes : []);
        setSourceOptions(Array.isArray(sources) ? sources : []);
      } catch (error) {
        console.error('Failed to load options:', error);
      }
    };

    loadOptions();
  }, []);

  // 加载角色数据（仅编辑他人时需要）
  useEffect(() => {
    if (isEditingOtherUser) {
      const loadRoles = async () => {
        try {
          const response = await RoleService.getRoles();
          if (response.success && response.data) {
            setRoles(response.data);
            if (targetUser && targetUser.roles) {
              form.setFieldsValue({ roles: targetUser.roles.map((role: any) => role.name) });
            }
          }
        } catch (error) {
          console.error('Failed to load roles:', error);
        }
      };
      loadRoles();
    }
  }, [isEditingOtherUser, targetUser, form]);

  // 加载“所属销售”候选：来自 sales 与 sales_manager 角色用户
  useEffect(() => {
    const loadSalesCandidates = async () => {
      try {
        const rolesRes = await RoleService.getRoles();
        if (!rolesRes.success || !Array.isArray(rolesRes.data)) return;
        const allRoles = rolesRes.data as any[];
        const targetRoles = allRoles.filter(r => ['sales', 'sales_manager'].includes(String(r.name)));
        const buckets: any[] = [];
        for (const r of targetRoles) {
          try {
            const res = await RoleService.getRoleUsers(r.id);
            if (res.success && Array.isArray(res.data)) buckets.push(...(res.data as any[]));
          } catch { }
        }
        const uniqueById = Object.values((buckets as any[]).reduce((acc: any, u: any) => {
          acc[u.id] = acc[u.id] || u;
          return acc;
        }, {}));
        setSalesCandidates(uniqueById as any[]);
      } catch {
        // 兜底：若无法获取角色用户，且当前用户为销售/经理，则仅提供当前用户
        try {
          const names = (user?.roles || []).map((r: any) => r?.name);
          if (names.includes('sales') || names.includes('sales_manager')) {
            setSalesCandidates([user]);
          } else {
            setSalesCandidates([]);
          }
        } catch {
          setSalesCandidates([]);
        }
      }
    };

    if (isEditingOtherUser && isCustomerRole) {
      loadSalesCandidates();
    }
  }, [isEditingOtherUser, isCustomerRole]);

  // 更新基本信息
  const handleProfileUpdate = async (values: UserProfileFormData) => {
    setLoading(true);
    try {
      const avatarValue = typeof values.avatar !== 'undefined' ? values.avatar : form.getFieldValue('avatar');
      const profileData: any = {
        username: values.username,
        email: values.email,
        phone: values.phone,
        client_type: values.client_type,
        customer_categories: values.customer_categories,
        contact_name: values.contact_name,
        source: values.source,
        group_number: values.group_number,
        avatar: typeof avatarValue === 'undefined' ? null : avatarValue,
      };
      if (typeof values.notification_emails !== 'undefined') {
        profileData.notification_emails = values.notification_emails;
      }

      let response;
      if (isEditingOtherUser) {
        if (Object.prototype.hasOwnProperty.call(values, 'parent_id')) {
          profileData.parent_id = typeof values.parent_id === 'undefined' ? undefined : (values.parent_id || null);
        }
        // 角色字段：直接透传（前端保证 roles 为数组）
        if (Object.prototype.hasOwnProperty.call((values as any), 'roles')) {
          const r = (values as any).roles;
          profileData.roles = Array.isArray(r) ? r : (r ? [r] : []);
        }
        // 编辑其他用户
        response = await UserService.updateUserProfile(parseInt(editingUserId!), profileData);
      } else {
        // 编辑自己
        response = await UserService.updateProfile(profileData);
      }

      if (response.success) {
        message.success(isEditingOtherUser ? '用户信息更新成功' : '个人信息更新成功');

        // 只有编辑自己时才更新本地用户信息
        if (!isEditingOtherUser && updateUserInfo) {
          updateUserInfo({
            ...user,
            username: values.username,
            display_name: values.contact_name,
            phone: values.phone,
            client_type: values.client_type,
            contact_name: values.contact_name,
            customer_categories: values.customer_categories,
            source: values.source,
            group_number: values.group_number,
            avatar: values.avatar,
          } as any);
          setAvatarPreview(values.avatar || avatarPreview);
        }
      } else {
        message.error(getApiResponseErrorMessage(response, '更新失败'));
      }
    } catch (error: any) {
      message.error(getAxiosErrorMessage(error, '更新失败'));
    } finally {
      setLoading(false);
    }
  };

  // 修改密码
  const handlePasswordChange = async (values: UserProfileFormData) => {
    setPasswordLoading(true);
    try {
      const response = await UserService.changePassword({
        current_password: values.current_password!,
        new_password: values.new_password!,
      });

      if (response.success) {
        message.success('密码修改成功');
        passwordForm.resetFields();
      } else {
        message.error(getApiResponseErrorMessage(response, '密码修改失败'));
      }
    } catch (error: any) {
      message.error(getAxiosErrorMessage(error, '密码修改失败'));
    } finally {
      setPasswordLoading(false);
    }
  };

  // 管理员分配角色：逻辑已并入基本信息保存

  // 管理员重置用户密码
  const handlePasswordReset = async (values: any) => {
    if (!isEditingOtherUser || !editingUserId) return;

    setResetPasswordLoading(true);
    try {
      const response = await UserService.updateUser(parseInt(editingUserId), {
        password: values.new_password
      });
      if (response.success) {
        message.success('密码重置成功');
        resetPasswordForm.resetFields();
      } else {
        message.error(getApiResponseErrorMessage(response, '密码重置失败'));
      }
    } catch (error: any) {
      message.error(getAxiosErrorMessage(error, '密码重置失败'));
    } finally {
      setResetPasswordLoading(false);
    }
  };

  if (!user) {
    return <div>加载中...</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={24}>
        <Col span={16}>

          {/* 基本信息（仅编辑） */}
          <Card title={isEditingOtherUser ? '编辑用户信息' : '基本信息'} style={{ marginBottom: '24px' }}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleProfileUpdate}
              autoComplete="off"
            >
              <Form.Item name="avatar" style={{ display: 'none' }}>
                <Input type="hidden" />
              </Form.Item>
              <Row gutter={16}>

                <Col span={8}>
                  <Form.Item
                    name="username"
                    label="用户名"
                    rules={[
                      { required: true, message: '请输入用户名' },
                      { min: 2, message: '用户名至少2个字符' },
                      { max: 50, message: '用户名不能超过50个字符' },
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      disabled={true}
                      placeholder="请输入您的姓名"
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="email"
                    label="电子邮箱"
                    rules={[
                      { required: true, message: '请输入电子邮箱' },
                      { type: 'email', message: '请输入有效的邮箱地址' },
                    ]}
                  >
                    <Input
                      placeholder="请输入电子邮箱"
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="phone"
                    label="电话"
                    rules={[
                      { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' },
                    ]}
                  >
                    <Input
                      prefix={<PhoneOutlined />}
                      placeholder="请输入手机号码"
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name="contact_name"
                    label="客户名称"
                  >
                    <Input
                      placeholder="请输入客户名称"
                    />
                  </Form.Item>
                </Col>

                {/* 角色选择在编辑他人区块展示 */}

                {isCustomerRole && (
                  <Col span={8}>
                    <Form.Item
                      name="client_type"
                      label="客户等级"
                    >
                      <Select
                        placeholder="请选择客户等级"
                        allowClear
                        showSearch
                      >
                        {clientTypeOptions.map(option => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {isCustomerRole && (
                  <Col span={8}>
                    <Form.Item
                      name="group_number"
                      label="客户群号"
                    >
                      <Input placeholder="请输入客户群号" />
                    </Form.Item>
                  </Col>
                )}

                {isCustomerRole && (
                  <Col span={8}>
                    <Form.Item
                      name="source"
                      label="客户来源"
                    >
                      <Select
                        placeholder="请选择客户来源"
                        allowClear
                        showSearch
                      >
                        {sourceOptions.map(option => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {isCustomerRole && (
                  <Col span={24}>
                    <Form.Item
                      name="customer_categories"
                      label="客户分类"
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="请选择客户分类"
                        options={CUSTOMER_CATEGORY_OPTIONS.map(v => ({ value: v, label: v }))}
                      />
                    </Form.Item>
                  </Col>
                )}

                {isCustomerRole && (
                  <Col span={24}>
                    <Form.Item
                      name="notification_emails"
                      label="通知邮箱"
                      extra="多个邮箱用 ; 分隔开"
                    >
                      <Input placeholder="例如: a@example.com; b@example.com" />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {/* 创建模式下选择上级已拆分到新建页面 */}

              {isEditingOtherUser && (
                <>
                  <Form.Item
                    name="roles"
                    label="用户角色"
                    rules={[{ required: true, message: '请选择一个角色' }]}
                  >
                    <Select placeholder="请选择角色" allowClear>
                      {roles.map(role => (
                        <Option key={role.id} value={role.name}>
                          {role.display_name || role.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {showParentSetting && (
                    <>
                      <Form.Item name="parent_id" label="选择上级用户">
                        <Select
                          placeholder="请选择上级用户（可选）"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                        >
                          {filteredParentCandidates.map((u: any) => (
                            <Option key={u.id} value={u.id}>
                              {(u as any).display_name || (u as any).username} ({u.email})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </>
                  )}

                  {isCustomerRole && (
                    <Form.Item name="parent_id" label="所属销售">
                      <Select
                        placeholder="请选择所属销售"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                      >
                        {salesCandidates.map((u: any) => (
                          <Option key={u.id} value={u.id}>
                            {(u as any).display_name || (u as any).username} ({u.email})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                >保存</Button>
              </Form.Item>
            </Form>
          </Card>

          {/* 已将角色管理卡片上移到顶部 */}

          {/* 密码重置 - 只有编辑其他用户时才显示 */}
          {isEditingOtherUser && (
            <Card title="密码重置" style={{ marginBottom: '24px' }}>
              <Form
                form={resetPasswordForm}
                layout="vertical"
                onFinish={handlePasswordReset}
                autoComplete="off"
              >
                <Form.Item
                  name="new_password"
                  label="新密码"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 8, message: '密码至少8个字符' },
                    { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/, message: '密码必须包含大小写字母和数字' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="请输入新密码"
                  />
                </Form.Item>

                <Form.Item
                  name="confirm_password"
                  label="确认新密码"
                  dependencies={['new_password']}
                  rules={[
                    { required: true, message: '请确认新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('new_password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="请再次输入新密码"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={resetPasswordLoading}
                  >
                    保存密码
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          {/* 修改密码 - 只有编辑自己时才显示 */}
          {!isEditingOtherUser && (
            <Card title="修改密码">
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handlePasswordChange}
                autoComplete="off"
              >
                <Form.Item
                  name="current_password"
                  label="当前密码"
                  rules={[
                    { required: true, message: '请输入当前密码' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="请输入当前密码"
                  />
                </Form.Item>

                <Form.Item
                  name="new_password"
                  label="新密码"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 8, message: '密码至少8个字符' },
                    { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/, message: '密码必须包含大小写字母和数字' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="请输入新密码"
                  />
                </Form.Item>

                <Form.Item
                  name="confirm_password"
                  label="确认新密码"
                  dependencies={['new_password']}
                  rules={[
                    { required: true, message: '请确认新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('new_password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="请再次输入新密码"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={passwordLoading}
                  >
                    保存密码
                  </Button>
                </Form.Item>
                {/* 合并：当编辑他人时，在同一个表单中提供角色与上级设置 */}
                {isEditingOtherUser && (
                  <>
                    <Form.Item
                      name="roles"
                      label="用户角色"
                      rules={[{ required: true, message: '请选择一个角色' }]}
                    >
                      <Select placeholder="请选择角色" allowClear>
                        {roles.map(role => (
                          <Option key={role.id} value={role.name}>
                            {role.display_name || role.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {showParentSetting && (
                      <>
                        <Form.Item name="parent_id" label="选择上级用户">
                          <Select
                            placeholder="请选择上级用户（可选）"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                          >
                            {parentCandidates.map((u: any) => (
                              <Option key={u.id} value={u.id}>
                                {(u as any).display_name || (u as any).username} ({u.email})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </>
                    )}
                  </>
                )}
              </Form>
            </Card>
          )}
        </Col>

        <Col span={8}>
          {/* 账户信息 */}
          <Card title={isEditingOtherUser ? '用户信息' : '账户信息'}>
            {isEditingOtherUser && (
              <div style={{ marginBottom: '16px', padding: '8px', background: '#f0f2ff', borderRadius: '4px', border: '1px solid #d6e4ff' }}>
                <Text type="warning" style={{ fontSize: '12px' }}>
                  正在编辑用户：{(targetUser as any)?.display_name || (targetUser as any)?.username || '加载中...'}
                </Text>
              </div>
            )}

            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  size={80}
                  src={isEditingOtherUser
                    ? ((targetUser as any)?.avatar || targetUser?.wechat_avatar)
                    : (avatarPreview || (user as any).avatar || user.wechat_avatar)}
                  icon={<UserOutlined />}
                  style={{ marginBottom: '16px' }}
                />
                {!isEditingOtherUser && (
                  <Button
                    size="small"
                    shape="circle"
                    icon={<EditOutlined />}
                    style={{ position: 'absolute', right: -6, bottom: 10 }}
                    onClick={() => setAvatarModalVisible(true)}
                  />
                )}
              </div>
              <div>
                <Title level={4} style={{ margin: '8px 0' }}>
                  {isEditingOtherUser ?
                    ((targetUser as any)?.display_name || (targetUser as any)?.username || '加载中...') :
                    (user.display_name || (user as any).username)
                  }
                </Title>
                <Text type="secondary">
                  {isEditingOtherUser ? (targetUser?.email || '加载中...') : user.email}
                </Text>
              </div>
            </div>

            <Divider />

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong>邮箱地址</Text>
                <br />
                <Text type="secondary">
                  {isEditingOtherUser ? (targetUser?.email || '加载中...') : user.email}
                </Text>
              </div>

              <div>
                <Text strong>用户角色</Text>
                <br />
                <Space wrap>
                  {(isEditingOtherUser ? targetUser?.roles : user.roles)?.map((role: any) => (
                    <Text
                      key={role.id}
                      style={{
                        background: '#f0f0f0',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {role.display_name || role.name}
                    </Text>
                  ))}
                </Space>
              </div>

              {(isEditingOtherUser ? targetUser?.wechat_nickname : user.wechat_nickname) && (
                <div>
                  <Text strong>微信信息</Text>
                  <br />
                  <Text type="secondary">
                    {isEditingOtherUser ? targetUser?.wechat_nickname : user.wechat_nickname}
                  </Text>
                </div>
              )}

              {!isEditingOtherUser && (
                <div>
                  <Text strong>微信开放平台绑定</Text>
                  <br />
                  <Space direction="vertical" size={4}>
                    <Text type="secondary">
                      {user.wechat_nickname ? `已绑定：${user.wechat_nickname}` : '未绑定微信，绑定后可使用扫码登录'}
                    </Text>
                    <Button size="small" type="primary" onClick={() => setWechatBindVisible(true)}>
                      {user.wechat_nickname ? '重新绑定微信' : '绑定微信'}
                    </Button>
                    {user.wechat_nickname && (
                      <Popconfirm
                        title="确定解除微信绑定？"
                        description="解除绑定后，您将不能通过微信扫码登录。"
                        okText="解除绑定"
                        cancelText="取消"
                        okButtonProps={{ danger: true, loading: unbindLoading }}
                        onConfirm={async () => {
                          setUnbindLoading(true);
                          try {
                            const res = await wechatService.unbindWeChat();
                            if (res.success) {
                              message.success('已解除微信绑定');
                              if (updateUserInfo) {
                                updateUserInfo({
                                  wechat_nickname: null as any,
                                  wechat_avatar: null as any,
                                } as any);
                              }
                            } else {
                              message.error(res.message || '解绑失败');
                            }
                          } catch (error) {
                            message.error('解绑失败，请稍后再试');
                          } finally {
                            setUnbindLoading(false);
                          }
                        }}
                      >
                        <Button size="small" danger loading={unbindLoading}>
                          解绑微信
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              )}

              {!isEditingOtherUser && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>公众号绑定</Text>
                  <br />
                  <Space direction="vertical" size={4}>
                    <Text type="secondary">
                      {(user as any).wechat_mp_openid ? '已绑定微信公众号' : '未绑定，绑定后可接收订单消息通知'}
                    </Text>
                    {!(user as any).wechat_mp_openid && (
                      <Button size="small" onClick={() => setWechatMpBindVisible(true)}>
                        关注公众号绑定
                      </Button>
                    )}
                    {(user as any).wechat_mp_openid && (
                      <Button size="small" type="dashed" onClick={() => setWechatMpBindVisible(true)}>
                        重新绑定
                      </Button>
                    )}
                  </Space>
                </div>
              )}


            </Space>
          </Card>
        </Col>
      </Row>

      {
        !isEditingOtherUser && (
          <>
            <WeChatLogin
              visible={wechatBindVisible}
              onClose={() => setWechatBindVisible(false)}
              onSuccess={(userInfo: any, token: string) => {
                if (token) {
                  authService.setToken(token);
                }
                if (updateUserInfo) {
                  updateUserInfo(userInfo);
                }
                message.success('微信绑定成功');
                setWechatBindVisible(false);
              }}
              mode="bind"
              bindCurrentUser
              title="绑定微信"
            />

            <WeChatLogin
              visible={wechatMpBindVisible}
              onClose={() => setWechatMpBindVisible(false)}
              onSuccess={(userInfo: any, token: string) => {
                if (token) {
                  authService.setToken(token);
                }
                if (updateUserInfo) {
                  updateUserInfo(userInfo);
                }
                message.success('公众号绑定成功');
                setWechatMpBindVisible(false);
              }}
              mode="bind"
              bindCurrentUser
              type="mp"
              title="关注公众号绑定"
            />
          </>
        )
      }

      {/* 头像编辑弹窗 */}
      <Modal
        title="编辑头像"
        open={avatarModalVisible}
        onCancel={() => setAvatarModalVisible(false)}
        onOk={() => {
          setAvatarModalVisible(false);
          form.submit();
        }}
        okText="保存头像"
        cancelText="取消"
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Avatar
            size={128}
            src={avatarPreview || (user as any).avatar || user.wechat_avatar}
            icon={<UserOutlined />}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <MediaSelector
            multiple={false}
            fileTypes={['image']}
            value={avatarMediaIds}
            showPreview={false}
            alwaysShowActions
            onChange={async (ids) => {
              const arr = Array.isArray(ids) ? ids : (ids ? [ids] : []);
              setAvatarMediaIds(arr as number[]);
              if (arr.length > 0) {
                try {
                  const res = await mediaService.getMedia(Number(arr[0]));
                  if (res.success && res.data) {
                    const url = mediaService.getMediaUrl(res.data);
                    form.setFieldsValue({ avatar: url });
                    setAvatarPreview(url);
                  }
                } catch (error) {
                  message.error('获取头像失败，请重试');
                }
              } else {
                form.setFieldsValue({ avatar: undefined });
                setAvatarPreview(undefined);
              }
            }}
          />
        </div>
      </Modal>

    </div >
  );
};

export default UserProfile;
